import React, { useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  FlatList,
} from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { RootStackParamList } from '../../types/navigation';
import { Ionicons } from '@expo/vector-icons';
import { palette } from '../../utils/palette';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

interface Language {
  id: string;
  name: string;
  nativeName: string;
  flag: string;
}

const languages: Language[] = [
  { id: 'en', name: 'English', nativeName: 'English', flag: '🇺🇸' },
  { id: 'es', name: 'Spanish', nativeName: 'Español', flag: '🇪🇸' },
  { id: 'fr', name: 'French', nativeName: 'Français', flag: '🇫🇷' },
  { id: 'de', name: 'German', nativeName: 'Deutsch', flag: '🇩🇪' },
  { id: 'it', name: 'Italian', nativeName: 'Italiano', flag: '🇮🇹' },
  { id: 'pt', name: 'Portuguese', nativeName: 'Português', flag: '🇵🇹' },
  { id: 'ru', name: 'Russian', nativeName: 'Русский', flag: '🇷🇺' },
  { id: 'zh', name: 'Chinese', nativeName: '中文', flag: '🇨🇳' },
  { id: 'ja', name: 'Japanese', nativeName: '日本語', flag: '🇯🇵' },
  { id: 'ko', name: 'Korean', nativeName: '한국어', flag: '🇰🇷' },
];

const LanguageSelectionScreen = () => {
  const navigation =
    useNavigation<NativeStackNavigationProp<RootStackParamList>>();
  const insets = useSafeAreaInsets();
  const [selectedLanguage, setSelectedLanguage] = useState('en');

  const handleLanguageSelect = (languageId: string) => {
    setSelectedLanguage(languageId);
    // Here you would typically update the app's language context/state
    // For example: i18n.changeLanguage(languageId);

    // Navigate back after selection
    setTimeout(() => {
      navigation.goBack();
    }, 300);
  };

  const renderLanguageItem = ({ item }: { item: Language }) => {
    const isSelected = selectedLanguage === item.id;

    return (
      <TouchableOpacity
        style={[styles.languageItem, isSelected && styles.selectedLanguageItem]}
        onPress={() => handleLanguageSelect(item.id)}
      >
        <View style={styles.languageContent}>
          <Text style={styles.languageFlag}>{item.flag}</Text>
          <View>
            <Text style={styles.languageName}>{item.name}</Text>
            <Text style={styles.languageNativeName}>{item.nativeName}</Text>
          </View>
        </View>
        {isSelected && (
          <Ionicons name="checkmark-circle" size={24} color="#7C3AED" />
        )}
      </TouchableOpacity>
    );
  };

  return (
    <View style={styles.container}>
      <FlatList
        data={languages}
        renderItem={renderLanguageItem}
        keyExtractor={(item) => item.id}
        contentContainerStyle={{
          paddingBottom: 40 + insets.bottom,
          paddingHorizontal: 20,
        }}
        showsVerticalScrollIndicator={false}
        ListHeaderComponent={() => (
          <View style={styles.infoSection}>
            <Ionicons
              name="information-circle-outline"
              size={24}
              color="#6B7280"
            />
            <Text style={styles.infoText}>
              Select your preferred language. This will change the language
              throughout the app.
            </Text>
          </View>
        )}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: palette.white,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingTop: 60,
    paddingBottom: 16,
    backgroundColor: palette.white,
    borderBottomWidth: 1,
    borderBottomColor: palette.veryLightGray,
  },
  backButton: {
    padding: 8,
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: palette.veryDarkBlue,
  },
  infoSection: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    padding: 20,
    backgroundColor: palette.softGray,
    marginVertical: 20,
    borderRadius: 12,
  },
  infoText: {
    fontSize: 14,
    color: palette.darkGrayBlue,
    marginLeft: 12,
    flex: 1,
  },
  languageItem: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 16,
    paddingHorizontal: 16,
    backgroundColor: palette.white,
    borderRadius: 12,
    marginBottom: 12,
    borderWidth: 1,
    borderColor: palette.veryLightGray,
  },
  selectedLanguageItem: {
    borderColor: palette.brightViolet,
    backgroundColor: palette.lightGrayViolet,
  },
  languageContent: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  languageFlag: {
    fontSize: 24,
    marginRight: 16,
  },
  languageName: {
    fontSize: 16,
    color: palette.veryDarkBlue,
    fontWeight: '500',
  },
  languageNativeName: {
    fontSize: 14,
    color: palette.darkGrayBlue,
  },
});

export default LanguageSelectionScreen;
